import React, { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useUsersStore } from '../../store/useUsersStore';
import { getAllCurrencies, searchCurrencies, formatCurrency } from '../../utils/currenciesHelper';


const CurrenciesFilter = ({ subUserId: propSubUserId }) => {
  // ============ STORE SUBSCRIPTIONS ============
  const users = useUsersStore((s) => s.users);
  const selectedKey = useUsersStore((s) => s.selectedKey);
  const updateSubUser = useUsersStore((s) => s.updateSubUser);
  const storeError = useUsersStore((s) => s.error);


  // ============ RESOLVE SUB-USER ID ============
  const resolvedSubUserId = useMemo(() => {
    if (propSubUserId) return String(propSubUserId);
    if (!selectedKey || !Array.isArray(users)) return null;
    
    const found = users.find((u) => {
      const id = u.sub_user_id || u.document_id || u.id;
      if (String(id) === String(selectedKey)) return true;
      if (u.sub_username && String(u.sub_username) === String(selectedKey)) return true;
      return false;
    });
    return found ? String(found.sub_user_id || found.document_id || found.id) : null;
  }, [propSubUserId, selectedKey, users]);


  const finalSubUserId = useMemo(
    () => String(resolvedSubUserId || propSubUserId || selectedKey || '') || null,
    [resolvedSubUserId, propSubUserId, selectedKey]
  );


  // ============ GET PROJECT FILTERS ============
  const projectFilters = useMemo(() => {
    if (!finalSubUserId || !Array.isArray(users)) return {};
    const u = users.find((x) => {
      const id = String(x.sub_user_id || x.document_id || x.id);
      return id === String(finalSubUserId);
    });
    return (u && u.project_filters) || {};
  }, [finalSubUserId, users]);


  // ============ GET ALL AVAILABLE CURRENCIES ============
  const allCurrencies = useMemo(() => getAllCurrencies(), []);


  // ============ INITIAL ALLOWED STATE WITH DUPLICATE REMOVAL ============
  const initialAllowed = useMemo(() => {
    const val = projectFilters.allowed_currencies || 
                projectFilters.currencies_allowed || 
                projectFilters.currencies;
    
    let rawArray = [];
    if (Array.isArray(val)) {
      rawArray = val.slice();
    } else if (typeof val === 'string' && val.trim()) {
      rawArray = val.split(',').map(s => s.trim()).filter(Boolean);
    }
    
    // Remove duplicates using Set
    return [...new Set(rawArray)];
  }, [projectFilters.allowed_currencies, projectFilters.currencies_allowed, projectFilters.currencies]);


  const initialAllowedJson = useMemo(() => JSON.stringify(initialAllowed || []), [initialAllowed]);


  // ============ LOCAL STATE ============
  const [allowed, setAllowed] = useState(initialAllowed);
  const [searchInput, setSearchInput] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const prevInitialRef = useRef(initialAllowedJson);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);


  // ============ SANITIZE ALLOWED ARRAY (Remove duplicates) ============
  const sanitizedAllowed = useMemo(() => {
    return [...new Set(allowed)];
  }, [allowed]);


  // ============ DETERMINE ACTIVE STATE ============
  const selectionState = useMemo(() => {
    if (sanitizedAllowed.length === 0) return 'none';
    if (sanitizedAllowed.length === allCurrencies.length) return 'all';
    return 'partial';
  }, [sanitizedAllowed.length, allCurrencies.length]);


  // ============ SYNC FROM STORE ============
  useEffect(() => {
    if (prevInitialRef.current !== initialAllowedJson) {
      setAllowed(initialAllowed);
      prevInitialRef.current = initialAllowedJson;
    }
    setMsg(null);
    setErr(null);
  }, [initialAllowedJson, resolvedSubUserId, initialAllowed]);


  // ============ FILTER CURRENCIES BASED ON SEARCH ============
  const filteredCurrencies = useMemo(() => {
    if (!searchInput.trim()) return [];
    return searchCurrencies(searchInput).filter(c => !sanitizedAllowed.includes(c.code));
  }, [searchInput, sanitizedAllowed]);


  // ============ HANDLERS ============
  const addCurrency = useCallback((currencyCode) => {
    setAllowed(prev => {
      const next = Array.isArray(prev) ? [...prev] : [];
      // Prevent duplicates
      if (!next.includes(currencyCode)) {
        next.push(currencyCode);
      }
      return next;
    });
    setSearchInput('');
    setIsDropdownOpen(false);
    inputRef.current?.focus();
  }, []);


  const removeCurrency = useCallback((currencyCode) => {
    setAllowed(prev => prev.filter(c => c !== currencyCode));
  }, []);


  const selectAll = useCallback(() => {
    // Use Set to ensure no duplicates
    setAllowed([...new Set(allCurrencies.map(c => c.code))]);
  }, [allCurrencies]);


  const clearAll = useCallback(() => {
    setAllowed([]);
    setSearchInput('');
    setIsDropdownOpen(false);
  }, []);


  const handleSubmit = useCallback(async () => {
    setErr(null);
    setMsg(null);
    if (!finalSubUserId) {
      setErr('No sub-user selected');
      return;
    }


    const payload = {
      project_filters: {
        ...(projectFilters || {}),
        // Save with duplicates removed
        allowed_currencies: [...new Set(allowed)],
      },
    };


    setSaving(true);
    try {
      await updateSubUser(finalSubUserId, payload);
      setMsg('✅ Saved successfully');
    } catch (e) {
      console.error('Currencies save failed', e);
      setErr(`❌ ${e?.message || 'Save failed'}`);
    } finally {
      setSaving(false);
    }
  }, [finalSubUserId, allowed, projectFilters, updateSubUser]);


  const handleReset = useCallback(() => {
    setAllowed(initialAllowed);
    setSearchInput('');
    setIsDropdownOpen(false);
    setMsg(null);
    setErr(null);
  }, [initialAllowed]);


  // ============ CLOSE DROPDOWN ON OUTSIDE CLICK ============
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && 
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  // ============ RENDER ============
  return (
    <div className="w-full p-6 bg-white rounded-lg shadow">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Allowed Currencies</h2>
        <p className="text-sm text-gray-600 mt-2">
          Select which currencies this sub-user accepts/targets for projects and bids.
        </p>
      </div>


      {/* Action Buttons with Active State Indicator */}
      <div className="flex flex-wrap gap-2 items-center mb-6">
        <div className="flex gap-2 mb-6 bg-white p-2 rounded-xl shadow-sm w-full border-2 border-light-blue">
          {/* Select All Button */}
          <button
            type="button"
            onClick={selectAll}
            disabled={saving}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 flex-1 ${
              selectionState === 'all'
                ? 'bg-primary-blue text-white shadow-md'
                : 'bg-light-blue-shade-20 text-primary-blue hover:bg-gray-50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Select All
          </button>


          {/* Active State Indicator (Middle Button) */}
          <div className="flex-1 px-6 py-2 rounded-lg font-medium transition-all duration-200 bg-primary-blue text-white shadow-md flex items-center justify-center">
            {selectionState === 'all' && (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                All Selected
              </span>
            )}
            {selectionState === 'none' && (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
                All Cleared
              </span>
            )}
            {selectionState === 'partial' && (
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd"/>
                </svg>
                Partial ({sanitizedAllowed.length}/{allCurrencies.length})
              </span>
            )}
          </div>


          {/* Clear All Button */}
          <button
            type="button"
            onClick={clearAll}
            disabled={saving}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 flex-1 ${
              selectionState === 'none'
                ? 'bg-primary-blue text-white shadow-md'
                : 'bg-light-blue-shade-20 text-primary-blue hover:bg-gray-50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Clear All
          </button>
        </div>


        {/* Status Messages */}
        <div className="ml-auto flex items-center gap-2">
          {saving && <span className="text-blue-600 text-sm font-medium">⏳ Saving...</span>}
          {msg && <span className="text-green-600 text-sm font-medium">{msg}</span>}
          {err && <span className="text-red-600 text-sm font-medium">{err}</span>}
          {storeError && !err && <span className="text-red-600 text-sm font-medium">{storeError}</span>}
        </div>
      </div>


      {/* Search Input - Primary Blue Border like Figma */}
      <div className="mb-6 relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Search & Add Currencies
        </label>


        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search currencies (e.g., USD, GBP, EUR)..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => searchInput && setIsDropdownOpen(true)}
            className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-2 focus:border-primary-blue"
          />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60">
                <img src="/Icons/search.svg" alt="Search" className="w-5 h-5" />
              </div>

          {/* Dropdown Results */}
          {isDropdownOpen && searchInput && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-primary-blue rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto"
            >
              {filteredCurrencies.length > 0 ? (
                filteredCurrencies.map(currency => (
                  <button
                    key={currency.code}
                    onClick={() => addCurrency(currency.code)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 text-sm font-medium text-gray-700 transition"
                  >
                    <div className="font-semibold text-primary-blue">{currency.code}</div>
                    <div className="text-xs text-gray-500">{currency.label}</div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-center text-gray-500 text-sm">
                  No currencies match "{searchInput}"
                </div>
              )}
            </div>
          )}
        </div>


        <p className="text-xs text-gray-500 mt-2">
          {sanitizedAllowed.length > 0
            ? `${sanitizedAllowed.length} currencies selected`
            : 'Start typing to search and add currencies'}
        </p>
      </div>


      {/* Selected Currencies - Styled like Figma excluded skills */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Included Currencies ({sanitizedAllowed.length})
        </label>


        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sanitizedAllowed.length === 0 ? (
            <div className="col-span-full text-sm text-gray-500">No currencies selected.</div>
          ) : (
            sanitizedAllowed.map((currencyCode) => {
              const currency = allCurrencies.find(c => c.code === currencyCode);
              return (
                <div 
                  key={currencyCode} 
                  className="flex items-center justify-between gap-3 rounded px-4 py-2 bg-light-blue border border-primary-blue" 
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-800">{currencyCode}</span>
                    {currency && (
                      <span className="text-xs text-gray-500">{currency.label}</span>
                    )}
                  </div>
                  <button
                    title="Remove"
                    onClick={() => removeCurrency(currencyCode)}
                    className="rounded flex items-center justify-center hover:bg-red-50 transition-colors"
                    disabled={saving}
                    style={{ width: 28, height: 28 }}
                  >
                    <img src="/Icons/cross-icon.svg" alt="Remove" className="w-4 h-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>


      {/* Footer Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleReset}
          disabled={saving}
          className="px-5 py-2 border-[1.5px] border-primary-blue text-primary-blue rounded-lg hover:bg-primary-blue hover:text-white disabled:opacity-60 transition font-medium"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || !finalSubUserId}
          className="px-6 py-2.5 bg-primary-blue text-white rounded-lg hover:bg-primary-blue/60 disabled:bg-gray-400 disabled:cursor-not-allowed transition font-medium"
        >
          {saving ? ' Saving...' : ' Save Changes'}
        </button>
      </div>
    </div>
  );
};


CurrenciesFilter.propTypes = {
  subUserId: PropTypes.string,
};


export default memo(CurrenciesFilter);
